import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  PaymentsPayoutService,
  type PayoutRail,
  type PayoutStatus,
} from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import {
  PaymentsPayinService,
  type PayinRail,
  type PayinRuntimeStatus,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { createAccessLatch } from "@/shared/utils/accessLatch";

import { ACTIVITY_PAGE_SIZE, LIST_POLL_INTERVAL_MS } from "../constants/paymentsActivity.constants";
import type {
  ActivityDirection,
  ActivityFiltersState,
  ActivityRow,
  ActivityScope,
} from "../types/paymentsActivity.types";
import { adaptPayinRow, adaptPayoutRow } from "../utils/activityAdapters";
import { scopeWithout, statusForDirection } from "../utils/activityLinks";

/**
 * Session-scoped access latch: a missing `payments:read` role surfaces as a 401 (the RBAC guard throws
 * Unauthorized, not Forbidden) and every 401 costs a silent `/auth/refresh` + replay inside HttpApiService —
 * after the first denial we stop asking for the rest of the session.
 *
 * `deniedKey` folds in the organization, which can arrive AFTER mount (a later `/auth/me` replaces the user
 * object), so the answer is read reactively rather than copied into state — see
 * `@/shared/utils/accessLatch`.
 */
const activityAccessLatch = createAccessLatch();

const INITIAL_FILTERS: ActivityFiltersState = { status: null, country: null, rangeDays: null, scope: null };

export interface UsePaymentsActivityResult {
  direction: ActivityDirection;
  setDirection: (direction: ActivityDirection) => void;
  filters: ActivityFiltersState;
  setStatusFilter: (status: PayinRuntimeStatus | PayoutStatus | null) => void;
  setCountryFilter: (country: string | null) => void;
  setRangeDays: (days: number | null) => void;
  /** Drops the scope a link brought in, keeping the toolbar's filters. */
  clearScope: () => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /** 1-indexed page over the offset-based backend pagination (`skip = (page-1) * limit`). */
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  total: number;
  rows: ActivityRow[];
  /** First load of the current query — the table shows the skeleton. Background refreshes never do. */
  isLoading: boolean;
  /** A background (polling / manual) refresh is in flight — subtle spinner only. */
  isRefreshing: boolean;
  /** Last successful fetch timestamp (for the "Actualizado hh:mm:ss" live caption). */
  lastUpdatedAt: Date | null;
  /** Non-access fetch failure — the caller renders an error alert, NEVER fabricated rows. */
  hasError: boolean;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  refresh: () => Promise<void>;
}

/**
 * Runtime payments activity list (payins OR payouts — two distinct endpoints with distinct status enums).
 * Live status = polling: the list silently refetches every 15 s only while at least one visible row is
 * non-terminal; it stops when everything settled and resumes on any query change or manual refresh.
 *
 * Tenancy: org members always query with their own `organization_id` (an org member without one gets no fetch
 * at all); INTERNAL users list cross-org (no param).
 *
 * `initialDirection` is applied on mount only — a deep link must land on the tab its operation belongs to,
 * but a later tab click by the user must not be fought by the URL it came from.
 */
export const usePaymentsActivity = (
  initialDirection: ActivityDirection = "payins",
  initialScope: { scope: ActivityScope | null; status: string | null } = { scope: null, status: null },
): UsePaymentsActivityResult => {
  const { user, isInternalUser } = useAuth();
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const deniedKey = `${user?.id ?? "anon"}:${organizationId ?? "no-org"}`;
  // Org members MUST be scoped to their org; internal staff list cross-org.
  const canQuery = isInternalUser || !!organizationId;

  const [direction, setDirectionState] = useState<ActivityDirection>(initialDirection);
  // A link's scope and status are applied once, on mount, like its direction.
  const [filters, setFilters] = useState<ActivityFiltersState>(() => ({
    ...INITIAL_FILTERS,
    // A status from another direction's vocabulary (a stale or hand-written URL) would be a 400.
    status: statusForDirection(initialScope.status, initialDirection) as
      | PayinRuntimeStatus
      | PayoutStatus
      | null,
    scope: initialScope.scope,
  }));
  const [page, setPageState] = useState(1);

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [settledQueryKey, setSettledQueryKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [hasError, setHasError] = useState(false);
  const hasAccess = !activityAccessLatch.useDenied(deniedKey);

  // The latest issued request wins — a stale slow response must never overwrite a newer one.
  const requestSeq = useRef(0);

  const queryKey = useMemo(
    () =>
      [
        direction,
        filters.status ?? "*",
        filters.country ?? "*",
        filters.rangeDays ?? "*",
        filters.scope ? JSON.stringify(filters.scope) : "*",
        page,
        organizationId ?? "cross-org",
      ].join("|"),
    [direction, filters, page, organizationId],
  );
  const isLoading = canQuery && hasAccess && settledQueryKey !== queryKey;

  const fetchPage = useCallback(
    async (options: { background: boolean }) => {
      if (!canQuery || activityAccessLatch.isDenied(deniedKey)) return;
      const seq = ++requestSeq.current;
      if (options.background) setIsRefreshing(true);
      // A query change after a failed read starts clean: show the skeleton for the new query, not the stale
      // error alert of the previous one. If this fetch fails too, the error comes right back.
      else setHasError(false);

      const scope = filters.scope;
      // A date option has already dropped the link's window from the scope (see `setRangeDays`).
      const window = filters.rangeDays
        ? {
            from: new Date(Date.now() - filters.rangeDays * 24 * 60 * 60 * 1000).toISOString(),
            to: undefined,
            dateField: undefined,
          }
        : { from: scope?.from ?? undefined, to: scope?.to ?? undefined, dateField: scope?.dateField };
      const common = {
        // Members stay pinned to their own organization whatever a link says.
        organizationId: isInternalUser ? (scope?.organizationId ?? null) : organizationId,
        country: filters.country ?? scope?.country ?? undefined,
        currency: scope?.currency ?? undefined,
        ...window,
        limit: ACTIVITY_PAGE_SIZE,
        skip: (page - 1) * ACTIVITY_PAGE_SIZE,
      };

      try {
        const next =
          direction === "payins"
            ? await PaymentsPayinService.listPayins({
                ...common,
                rail: (scope?.rail as PayinRail | null) ?? undefined,
                status: (filters.status as PayinRuntimeStatus | null) ?? undefined,
              }).then((res) => ({
                rows: res.data.data.map(adaptPayinRow),
                total: res.data.total,
              }))
            : await PaymentsPayoutService.listPayouts({
                ...common,
                rail: (scope?.rail as PayoutRail | null) ?? undefined,
                sourceCurrency: scope?.sourceCurrency ?? undefined,
                status: (filters.status as PayoutStatus | null) ?? undefined,
              }).then((res) => ({
                rows: res.data.data.map(adaptPayoutRow),
                total: res.data.total,
              }));

        if (seq !== requestSeq.current) return;
        // Swap data in place — background refreshes never blank the table.
        setRows(next.rows);
        setTotal(next.total);
        setHasError(false);
        setLastUpdatedAt(new Date());
        setSettledQueryKey(queryKey);
      } catch (err: unknown) {
        if (seq !== requestSeq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          activityAccessLatch.latch(deniedKey);
        } else if (!options.background) {
          // Money display rule: a failed first read shows the error state, never an empty list as truth.
          setHasError(true);
          setRows([]);
          setTotal(0);
          setSettledQueryKey(queryKey);
        }
        // Background refresh failures keep the last known-good page silently.
      } finally {
        if (seq === requestSeq.current) setIsRefreshing(false);
      }
    },
    [canQuery, deniedKey, direction, filters, isInternalUser, organizationId, page, queryKey],
  );

  // Foreground fetch on every query change.
  useEffect(() => {
    void fetchPage({ background: false });
  }, [fetchPage]);

  // Keep the page in range when the dataset shrinks under us (background refresh / rows settling elsewhere) —
  // otherwise the user is stranded on an empty page past the end.
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / ACTIVITY_PAGE_SIZE));
    if (page > maxPage) setPageState(maxPage);
  }, [page, total]);

  // Live status: poll only while at least one visible row can still move.
  const hasNonTerminalRows = rows.some((row) => row.isNonTerminal);
  useEffect(() => {
    if (!hasNonTerminalRows || !hasAccess || !canQuery) return;
    const interval = setInterval(() => {
      void fetchPage({ background: true });
    }, LIST_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasNonTerminalRows, hasAccess, canQuery, fetchPage]);

  // Instant refresh when the runtime announces a flip, with the poll above as the safety net (a dropped
  // socket only costs the usual 15 s). Refetching the page — rather than patching a row — keeps the
  // server's filters, ordering and pagination authoritative.
  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  });
  useEffect(() => {
    if (!hasAccess || !canQuery) return;
    const refresh = () => void fetchPageRef.current({ background: true });
    const offPayin = notificationsSocketService.onPayinUpdate(refresh);
    const offPayout = notificationsSocketService.onPayoutUpdate(refresh);
    return () => {
      offPayin();
      offPayout();
    };
  }, [hasAccess, canQuery]);

  const setDirection = useCallback((next: ActivityDirection) => {
    setDirectionState(next);
    // The status vocabularies differ per direction — a carried-over value would be invalid.
    setFilters((prev) => ({ ...prev, status: null }));
    setPageState(1);
  }, []);
  const setStatusFilter = useCallback((status: PayinRuntimeStatus | PayoutStatus | null) => {
    setFilters((prev) => ({ ...prev, status }));
    setPageState(1);
  }, []);
  // Any corridor option — one, or "all" — takes over the link's corridor (rail, country, currencies).
  const setCountryFilter = useCallback((country: string | null) => {
    setFilters((prev) => ({
      ...prev,
      country,
      scope: prev.scope ? scopeWithout(prev.scope, "corridor") : null,
    }));
    setPageState(1);
  }, []);
  // Any date option — a preset, or "all dates" — takes over the link's window.
  const setRangeDays = useCallback((rangeDays: number | null) => {
    setFilters((prev) => ({
      ...prev,
      rangeDays,
      scope: prev.scope ? scopeWithout(prev.scope, "window") : null,
    }));
    setPageState(1);
  }, []);
  const clearScope = useCallback(() => {
    setFilters((prev) => ({ ...prev, scope: null }));
    setPageState(1);
  }, []);
  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPageState(1);
  }, []);

  const refresh = useCallback(() => fetchPage({ background: true }), [fetchPage]);

  return {
    direction,
    setDirection,
    filters,
    setStatusFilter,
    setCountryFilter,
    setRangeDays,
    clearScope,
    clearFilters,
    hasActiveFilters:
      filters.status !== null ||
      filters.country !== null ||
      filters.rangeDays !== null ||
      filters.scope !== null,
    page,
    setPage: setPageState,
    pageSize: ACTIVITY_PAGE_SIZE,
    total,
    rows,
    isLoading,
    isRefreshing,
    lastUpdatedAt,
    hasError,
    hasAccess: hasAccess && canQuery,
    refresh,
  };
};
